import type { CSSProperties, ReactNode } from 'react';
import { renderMentions } from './mentions';
import { marcarAcronimosHtml, quitarMarcasDeAcronimos } from './acronimos';
import { localizeSystemMessage } from './alertI18n';

/**
 * UNA sola forma de pintar el cuerpo de un mensaje interno (LEY dos caminos, una fórmula): la usan
 * el Messaging de oficina y el InspectorMessages del teléfono. Antes cada pantalla tenía su copia y
 * la del inspector solo resaltaba @menciones: un DM del sistema con link markdown —el "missed share"
 * `[Ver](/inspector?share=12)` o un `[Watch](/rec/12)`— le salía como texto crudo.
 *
 *  - `looksHtml`/`sanitizeHtml`: el body del editor rico (formato, GIFs) con el allowlist de los
 *    updates del claim.
 *  - `toAppPath`: deja RELATIVO todo link que apunte a este app (cubre los DM viejos de prod:
 *    absolutos y los rotos sin protocolo de la época del APP_BASE_URL mal puesto).
 *  - `renderBody`: links markdown `[texto](url)` → <a>; el resto pasa por `renderMentions`.
 */

/** ¿El body viene del editor rico (HTML con formato/GIFs)? Los mensajes viejos son texto plano. */
export const looksHtml = (s: string) => /<(b|i|u|s|strike|em|strong|ul|ol|li|div|p|br|img|span)[\s/>]/i.test(s);

/**
 * Sanitiza el HTML del editor rico (mismo allowlist de etiquetas que los updates del claim).
 *
 * NO es un regex sobre el texto original: cada etiqueta se PARSEA (nombre + atributos) y se vuelve a
 * ESCRIBIR desde cero solo con lo permitido; lo que no parsea como etiqueta sale escapado (`&lt;`).
 * Así el navegador solo ve etiquetas que armamos nosotros. El saneado anterior (tres `replace`) se
 * saltaba con `<img/onerror=…>` (exigía un espacio antes de `on`), con `src="x"onerror=…` (la comilla
 * pegada) y con `href="&#106;avascript:…"` (la entidad tapaba `javascript:`) — verificado en node, y
 * el servidor no sanea el body, así que cualquier rol del tenant podía ejecutar script en la sesión
 * de otro (el teléfono del inspector incluido, que antes pintaba texto plano).
 *
 * Atributos que sobreviven: `href` (solo en <a>), `src` (solo en <img>), `alt`, `title`, `class`
 * (tokens simples) y `style` (declaraciones sin `url(`, `expression`, `javascript`, `@import`, `\`).
 * Las URLs se decodifican (entidades) y se limpian de controles ANTES de mirar el esquema:
 * http(s), mailto, tel y relativas en `href`; http(s) y `data:image/…` en `src`.
 */
const ALLOWED_TAGS = new Set(['br', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'a', 'p', 'div', 'span', 'ul', 'ol', 'li', 'img']);
const VOID_TAGS = new Set(['br', 'img']);
const NAMED_ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', tab: '\t', newline: '\n', colon: ':' };

/** Decodifica entidades numéricas y las nombradas de uso común (lo que basta para leer un esquema). */
function decodeEntities(v: string): string {
  return v.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);?/gi, (m, e: string) => {
    if (e[0] === '#') {
      const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : m;
    }
    const named = NAMED_ENTITIES[e.toLowerCase()];
    return named ?? m;
  });
}

const escapeAttr = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** URL segura para `href`/`src`, o null. Se limpia y decodifica ANTES de mirar el esquema. */
function safeUrl(raw: string, kind: 'href' | 'src'): string | null {
  // Los navegadores ignoran tab/salto y los controles al leer una URL: `jav\tascript:` es javascript:.
  // eslint-disable-next-line no-control-regex -- a propósito: se quitan los caracteres de control ANTES de mirar el esquema
  const v = decodeEntities(raw).replace(/[\u0000-\u0020\u007f-\u009f]/g, '');
  if (!v) return null;
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(v)?.[1]?.toLowerCase() ?? null;
  if (scheme == null) return v; // relativa (/claims/33, #x, ?q) o sin esquema
  if (kind === 'href') return scheme === 'http' || scheme === 'https' || scheme === 'mailto' || scheme === 'tel' ? v : null;
  if (scheme === 'http' || scheme === 'https') return v;
  return /^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=]+$/i.test(v) ? v : null;
}

function safeStyle(raw: string): string | null {
  const decls: string[] = [];
  for (const part of decodeEntities(raw).split(';')) {
    const idx = part.indexOf(':');
    if (idx < 0) continue;
    const prop = part.slice(0, idx).trim(); const val = part.slice(idx + 1).trim();
    if (!/^[a-z-]+$/i.test(prop) || !val) continue;
    if (/url\s*\(|expression|javascript|@import|\\|[<>]|\/\*/i.test(val)) continue;
    decls.push(`${prop}:${val}`);
  }
  return decls.length ? decls.join(';') : null;
}

function safeClass(raw: string): string | null {
  const tokens = raw.split(/\s+/).filter((c) => /^[\w-]+$/.test(c));
  return tokens.length ? tokens.join(' ') : null;
}

/** Atributo de salida para (etiqueta, nombre, valor crudo); null = se descarta. */
function keepAttr(tag: string, name: string, value: string): string | null {
  switch (name) {
    case 'href': return tag === 'a' ? safeUrl(value, 'href') : null;
    case 'src': return tag === 'img' ? safeUrl(value, 'src') : null;
    // eslint-disable-next-line no-control-regex -- a propósito: sin caracteres de control en atributos de texto
    case 'alt': case 'title': return decodeEntities(value).replace(/[\u0000-\u001f\u007f]/g, '');
    case 'class': return safeClass(value);
    case 'style': return safeStyle(value);
    default: return null;
  }
}

export function sanitizeHtml(h: string): string {
  const len = h.length;
  let out = '';
  let i = 0;
  while (i < len) {
    const lt = h.indexOf('<', i);
    if (lt < 0) { out += h.slice(i); break; }
    out += h.slice(i, lt);
    // Comentarios, <!doctype>, <?…>: se descartan enteros.
    if (h.startsWith('<!--', lt)) { const end = h.indexOf('-->', lt + 4); i = end < 0 ? len : end + 3; continue; }
    if (h[lt + 1] === '!' || h[lt + 1] === '?') { const end = h.indexOf('>', lt); i = end < 0 ? len : end + 1; continue; }
    const m = /^<(\/?)([a-zA-Z][a-zA-Z0-9-]*)/.exec(h.slice(lt, lt + 40));
    if (!m) { out += '&lt;'; i = lt + 1; continue; }
    const closing = m[1] === '/';
    const tag = m[2]!.toLowerCase();
    let pos = lt + m[0].length;
    const attrs: Array<[string, string]> = [];
    let terminated = false;
    while (pos < len) {
      // Espacios y `/` separan atributos (`<img/onerror=…>` es `img` + `onerror`, como en HTML5).
      while (pos < len && /[\s/]/.test(h[pos]!)) pos += 1;
      if (pos >= len) break;
      if (h[pos] === '>') { pos += 1; terminated = true; break; }
      const nm = /^[^\s"'>/=]+/.exec(h.slice(pos));
      if (!nm) { pos += 1; continue; } // comilla o `=` suelto
      const name = nm[0].toLowerCase();
      pos += nm[0].length;
      while (pos < len && /\s/.test(h[pos]!)) pos += 1;
      let value = '';
      if (h[pos] === '=') {
        pos += 1;
        while (pos < len && /\s/.test(h[pos]!)) pos += 1;
        const q = h[pos];
        if (q === '"' || q === "'") {
          const end = h.indexOf(q, pos + 1);
          if (end < 0) { value = h.slice(pos + 1); pos = len; }
          else { value = h.slice(pos + 1, end); pos = end + 1; }
        } else {
          const uv = /^[^\s>]*/.exec(h.slice(pos));
          value = uv?.[0] ?? '';
          pos += value.length;
        }
      }
      attrs.push([name, value]);
    }
    if (!terminated) { out += '&lt;'; i = lt + 1; continue; } // `<` sin cierre: texto
    i = pos;
    if (!ALLOWED_TAGS.has(tag)) continue; // etiqueta fuera del allowlist: se quita, el contenido queda
    if (closing) { if (!VOID_TAGS.has(tag)) out += `</${tag}>`; continue; }
    let attrString = '';
    const seen = new Set<string>();
    for (const [name, value] of attrs) {
      if (seen.has(name)) continue;
      seen.add(name);
      const kept = keepAttr(tag, name, value);
      if (kept != null) attrString += ` ${name}="${escapeAttr(kept)}"`;
    }
    out += `<${tag}${attrString}>`;
  }
  return out;
}

/**
 * HTML saneado Y con los acrónimos de la oficina marcados (Settings → Acronyms): SOLO para pintar
 * con dangerouslySetInnerHTML. `sanitizeHtml` queda PURO a propósito: también siembra el editor al
 * editar una nota, y del 09-24 al 09-29 marcaba ahí el <abbr>, el editor lo devolvía en su innerHTML
 * y quince notas se guardaron con la marca adentro (salían como `&lt;abbr class="acr"…`).
 * Un <abbr> que venga guardado de esa época se quita antes (no está en el allowlist, pero en un
 * body sin otro HTML `looksHtml` es falso y se pintaba escapado como texto).
 */
export function htmlParaPintar(h: string): string {
  return marcarAcronimosHtml(sanitizeHtml(quitarMarcasDeAcronimos(h)));
}

/**
 * Deja el link en forma RELATIVA si apunta a este mismo app. Cubre los DM viejos de prod:
 * absolutos (https://app.claimsproactive.com/claims/33) y los rotos sin protocolo
 * (app.claimsproactive.com/claims/33 — la época del APP_BASE_URL mal puesto).
 * Un `//host/...` (protocolo-relativo) NO es del app aunque empiece por `/`: se resuelve como absoluto
 * y pasa por la misma comprobación de host — antes se tomaba como ruta interna y `window.open` /
 * `location.assign` sacaban al que tocaba a un dominio ajeno con estilo de link interno.
 */
export function toAppPath(url: string): string | null {
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  try {
    const abs = url.startsWith('//') ? `https:${url}` : /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const u = new URL(abs);
    if (u.host === window.location.host || /(^|\.)claimsproactive\.com$/i.test(u.host)) {
      return `${u.pathname}${u.search}${u.hash}`;
    }
  } catch { /* no es URL */ }
  return null;
}

/**
 * `onDark` = el texto va dentro de la burbuja propia (fondo azul/verde). Los links se pintan en
 * blanco: con el color de acento, sobre la burbuja, el nombre del cliente quedaba invisible.
 */
export function renderBody(body: string, onDark = false): ReactNode {
  const parts: ReactNode[] = [];
  // Links markdown ABSOLUTOS (https://…), RELATIVOS (/claims/33?tab=overview#msg-769) y los
  // legacy sin protocolo (app.claimsproactive.com/…) — estos últimos salían como texto plano.
  const linkRe = /\[([^\]]+)\]\(((?:https?:\/\/|\/|[a-z0-9-]+(?:\.[a-z0-9-]+)+\/)[^)\s]+)\)/gi;
  const style = onDark
    ? { color: '#fff', textDecoration: 'underline', fontWeight: 700 } as const
    : { color: 'var(--accent)', textDecoration: 'underline', fontWeight: 600 } as const;
  let last = 0; let m: RegExpExecArray | null; let i = 0;
  while ((m = linkRe.exec(body))) {
    if (m.index > last) parts.push(...renderMentions(body.slice(last, m.index), `t${i}`));
    const url = m[2]!;
    const appPath = toAppPath(url);
    // Links del app: intenta PESTAÑA NUEVA (el chat se queda abierto — pedido del equipo) y si
    // el navegador la bloquea (PWA instalado / móvil: reporte de Brenda "no abre"), navega
    // DENTRO del app al mismo destino. El click nunca queda muerto.
    parts.push(appPath
      ? <a key={`l${i}`} href={appPath} style={style}
          onClick={(e) => { e.preventDefault(); const w = window.open(appPath, '_blank', 'noopener'); if (!w) window.location.assign(appPath); }}>
          {m[1]}
        </a>
      : <a key={`l${i}`} href={url} target="_blank" rel="noreferrer" style={style}>{m[1]}</a>);
    last = m.index + m[0].length; i++;
  }
  if (last < body.length) parts.push(...renderMentions(body.slice(last), `t${i}`));
  return parts;
}

/**
 * El cuerpo completo: HTML del editor rico saneado, o texto plano con los mensajes del sistema
 * traducidos (`localizeSystemMessage`) + links + menciones. Un solo <div> para que cada pantalla
 * le ponga su clase/estilo de burbuja.
 */
/**
 * LINKS MARKDOWN DENTRO DE UN CUERPO HTML (reporte del dueño 2026-09-11: los DM de mención salían
 * con "for [Patricia Ruiz & Ernesto Palacios](/claims/2912?tab=scheduling#msg-107968)" crudo).
 * El DM de una mención es `💬 {texto del update} · for [cliente](url)`; si el update venía del editor
 * rico (chips de @mención), el cuerpo "parece HTML" y tomaba el camino saneado, donde nadie
 * convertía el link. Se convierte ANTES de sanear (el saneador conserva <a href> con rutas del
 * app). Las rutas del app abren en pestaña nueva, igual que en el camino de texto.
 */
export function linkifyMarkdownInHtml(html: string): string {
  return html.replace(/\[([^\]\n]+)\]\(((?:https?:\/\/|\/|[a-z0-9-]+(?:\.[a-z0-9-]+)+\/)[^)\s"<>]+)\)/gi, (_m, texto: string, url: string) => {
    const appPath = toAppPath(url);
    const href = appPath ?? url;
    return `<a href="${href.replace(/"/g, '&quot;')}" target="_blank" rel="noreferrer">${texto}</a>`;
  });
}

export function MessageBody({ body, onDark = false, lang, className, style }: {
  body: string; onDark?: boolean; lang: string; className?: string; style?: CSSProperties;
}) {
  const cuerpo = quitarMarcasDeAcronimos(body);
  if (looksHtml(cuerpo)) {
    return <div className={className} style={{ ...style, whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: htmlParaPintar(linkifyMarkdownInHtml(cuerpo)) }} />;
  }
  return <div className={className} style={style}>{renderBody(localizeSystemMessage(cuerpo, lang), onDark)}</div>;
}
